import {
  Button,
  ButtonVariant,
  Input,
  InputGroup,
  type SelectOption,
  Spinner,
} from '@aus-platform/design-system';
import classNames from 'classnames';
import { isEmpty, isNil } from 'lodash';
import React, { type FormEvent, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import {
  HeapCategory,
  HeapMaterialType,
  handleResponseMessage,
  useHeap,
  useUpdateHeap,
} from 'shared/api';
import { Formatter, preventNonNumber } from 'shared/helpers';
import { useInputFields } from 'shared/hooks';
import {
  getMaterialTypeLabel,
  materialTypeOptions,
  volumeCategoryOptions,
} from '../../constants';
import { editHeapValidator } from './edit-heap-validators';
import type { EditHeapProps, InitialInputStateType } from './types';

const initialInputState: InitialInputStateType = {
  name: '',
  description: '',
  bulkDensity: null,
  category: {
    label: '',
    value: HeapCategory.Other,
  },
  materialType: {
    label: '',
    value: HeapMaterialType.Other,
  },
};

export const EditHeap: React.FC<EditHeapProps> = ({
  onClose,
  heapId,
  refetchHeapList,
  setIsHeapClusterModified,
  show,
}) => {
  //Hooks.
  const {
    values,
    names,
    onBlur,
    onChange,
    setValues,
    onFocus,
    inputHasError,
    inputIsDirty,
    setDirty,
    resetAll,
  } = useInputFields<InitialInputStateType>(
    initialInputState,
    editHeapValidator,
  );

  // Api's
  const {
    data: heapResponse,
    isSuccess: isHeapResponseSuccess,
    isInitialLoading: heapResponseIsLoading,
    refetch: refetchHeapResponse,
  } = useHeap(heapId, false);

  const {
    mutate: sendUpdateHeapRequest,
    data: heapUpdateResponse,
    isError: isErrorUpdateHeap,
    isSuccess: isSuccessUpdateHeap,
    error: updateHeapErrorResponse,
  } = useUpdateHeap();

  // useEffect's
  useEffect(() => {
    if (isHeapResponseSuccess && heapResponse) {
      const { name, remarks, bulkDensity, materialType, category } =
        heapResponse.data;
      setValues({
        name: name,
        description: remarks ?? '',
        bulkDensity: bulkDensity,
        materialType: {
          label: getMaterialTypeLabel(materialType),
          value: materialType,
        },
        category: {
          label: Formatter.toTitleCase(category, '_'),
          value: category,
        },
      });
    }
  }, [isHeapResponseSuccess, heapResponse]);

  useEffect(() => {
    if (heapId && show) {
      refetchHeapResponse();
    }
  }, [heapId, show]);

  useEffect(() => {
    if (isSuccessUpdateHeap && heapUpdateResponse) {
      handleResponseMessage(
        isSuccessUpdateHeap,
        isErrorUpdateHeap,
        heapUpdateResponse,
        updateHeapErrorResponse,
      );
      refetchHeapList();
      setIsHeapClusterModified(true);
      onCloseModal();
    }
  }, [isSuccessUpdateHeap, isErrorUpdateHeap]);

  // Conditional variables.
  const modalBodyCustomClass = classNames([
    heapResponseIsLoading ? 'modal-response-loading' : '',
  ]);

  //Handlers.
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const { name, description, bulkDensity, materialType, category } = values;

    let weight;
    if (!isNil(bulkDensity) && heapResponse && heapResponse.data.cutVolume) {
      weight = (heapResponse.data.cutVolume * bulkDensity).toFixed(3);
    } else {
      weight = heapResponse?.data.cutWeight;
    }
    const remarks = isEmpty(description) ? null : description;
    sendUpdateHeapRequest({
      id: heapId,
      name: name,
      remarks: remarks,
      bulkDensity: bulkDensity,
      weight: weight,
      category: category.value,
      materialType: materialType.value,
    });
  };

  const onCloseModal = () => {
    resetAll();
    onClose();
    setValues(initialInputState);
  };

  const handleSelectChange =
    (field: string) => (selectedOption: SelectOption) => {
      setValues((prev) => ({
        ...prev,
        [field]: selectedOption,
      }));

      setDirty((prev) => ({
        ...prev,
        [field]: true,
      }));
    };

  return (
    <Modal
      className="edit-heap body-txt-3"
      contentClassName="w-40"
      show={show}
      restoreFocus={false}
      centered
      aria-labelledby="contained-modal-title-hcenter"
      onHide={onCloseModal}
    >
      <Modal.Header closeButton> Edit Heap Cluster </Modal.Header>
      <form onSubmit={onSubmit}>
        <Modal.Body className={modalBodyCustomClass}>
          {heapResponseIsLoading ? (
            <Spinner />
          ) : (
            <div className="edit-heap-form">
              <InputGroup>
                <Input.Label> Name </Input.Label>
                <Input.Text
                  placeholder="Heap Name"
                  value={values.name}
                  name={names.name}
                  {...{ onChange, onBlur, onFocus }}
                />
              </InputGroup>
              <div className="edit-heap-form__category-container">
                <InputGroup className="edit-heap-form__select">
                  <Input.Label>Heap Cluster Category</Input.Label>
                  <Input.Select
                    value={values.category}
                    placeholder="Heap Category"
                    name={names.category}
                    options={volumeCategoryOptions}
                    onChange={handleSelectChange('category')}
                  />
                </InputGroup>
                <InputGroup className="edit-heap-form__select">
                  <Input.Label>Heap Cluster Material</Input.Label>
                  <Input.Select
                    value={values.materialType}
                    placeholder="Heap Material"
                    name={names.materialType}
                    options={materialTypeOptions}
                    onChange={handleSelectChange('materialType')}
                  />
                </InputGroup>
              </div>
              <div className="edit-heap-form__description-container">
                <InputGroup>
                  <Input.Label> Description </Input.Label>
                  <Input.Text
                    placeholder="Heap Cluster Description"
                    value={values.description}
                    name={names.description}
                    maxLength={200}
                    {...{ onChange, onBlur, onFocus }}
                  />
                </InputGroup>
                <InputGroup className="edit-heap-form__density">
                  <Input.Label> Bulk Density (gm/cm³) </Input.Label>
                  <Input.Number
                    value={values.bulkDensity ?? undefined}
                    placeholder="Bulk Density"
                    name={names.bulkDensity}
                    onKeyDown={preventNonNumber}
                    min={0}
                    step="any"
                    {...{ onChange, onBlur, onFocus }}
                  />
                </InputGroup>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="edit-heap__buttons">
            <Button variant={ButtonVariant.Secondary} onClick={onCloseModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={inputHasError() || !inputIsDirty()}>
              Save
            </Button>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
